// src/pages/TourStatements/TourStatementsView.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import tourStatementService from '../../services/tourStatement.service';
import farmerVisitService from '../../services/farmerVisit.service';
import Toast from '../../components/Toast/Toast';
import './TourStatements.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const TourStatementsView = () => {
  const navigate  = useNavigate();
  const { userId } = useParams();
  const location  = useLocation();
  const { employeeName, companyId, companyName } = location.state || {};

  const [statements, setStatements]       = useState([]);
  const [farmerVisits, setFarmerVisits]   = useState([]);
  const [statementsLoading, setStatementsLoading] = useState(false);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [activeTab, setActiveTab]         = useState('tours'); // 'tours' | 'visits'
  const [toast, setToast]                 = useState(null);

  useEffect(() => {
    fetchTourStatements();
    fetchFarmerVisits();
  }, [userId]);

  const fetchTourStatements = async () => {
    setStatementsLoading(true);
    try {
      const response = await tourStatementService.getTourStatementsByUser(userId);
      if (response.success && response.data) {
        setStatements(response.data);
      }
    } catch (error) {
      setToast({ message: 'Failed to fetch tour statements', type: 'error' });
    } finally {
      setStatementsLoading(false);
    }
  };

  const fetchFarmerVisits = async () => {
    setVisitsLoading(true);
    try {
      const response = await farmerVisitService.getVisitsForStatement(userId);
      if (response.success && response.data) {
        setFarmerVisits(response.data);
      }
    } catch (error) {
      console.error('Error fetching farmer visits:', error);
      // Non-fatal — some roles may not have farmer visits
    } finally {
      setVisitsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/orders/tour-statements/employees/${companyId}`, {
      state: { companyName },
    });
  };

  return (
    <div className="tour-statements-page">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Header */}
      <div className="tour-header">
        <h4 className="tour-title">Tour Statements</h4>
      </div>

      {/* Back Button */}
      <button className="btn-back" onClick={handleBack}>
        ← Back
      </button>

      {/* Employee name */}
      <h5 className="section-subtitle" style={{ marginBottom: 16 }}>
        {employeeName}
      </h5>

      {/* Tabs */}
      <div className="ts-tabs">
        <button
          className={`ts-tab ${activeTab === 'tours' ? 'active' : ''}`}
          onClick={() => setActiveTab('tours')}
        >
          Tour Entries
          {statements.length > 0 && (
            <span className="ts-tab-badge">{statements.length}</span>
          )}
        </button>
        <button
          className={`ts-tab ${activeTab === 'visits' ? 'active' : ''}`}
          onClick={() => setActiveTab('visits')}
        >
          Farmer Visits
          {farmerVisits.length > 0 && (
            <span className="ts-tab-badge">{farmerVisits.length}</span>
          )}
        </button>
      </div>

      {/* ── Tour Entries Tab ── */}
      {activeTab === 'tours' && (
        <div className="statements-section">
          <div className="statements-table-container">
            {statementsLoading ? (
              <div className="loading">Loading tour statements...</div>
            ) : (
              <table className="statements-table">
                <thead>
                  <tr>
                    <th>DATE</th>
                    <th>TYPE</th>
                    <th>START KM</th>
                    <th>END KM</th>
                    <th>TOTAL KM</th>
                    <th>TRANSPORT</th>
                    <th>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.length > 0 ? (
                    statements.map((statement) => (
                      <tr key={statement.id}>
                        <td>{formatDate(statement.tourDate)}</td>
                        <td>
                          <span className={`ts-badge ${statement.tourType?.toLowerCase()}`}>
                            {statement.tourType || '—'}
                          </span>
                        </td>
                        <td>{statement.startKm ?? '—'}</td>
                        <td>{statement.endKm   ?? '—'}</td>
                        <td className="total-km">
                          {statement.totalKm != null ? `${statement.totalKm} km` : '—'}
                        </td>
                        <td>
                          {statement.modeOfTransport
                            ? statement.modeOfTransport.replace('_', ' ')
                            : '—'}
                        </td>
                        <td>
                          <span className={`ts-badge ${statement.status?.toLowerCase()}`}>
                            {statement.status || '—'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-data">
                        No tour statements available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Farmer Visits Tab ── */}
      {activeTab === 'visits' && (
        <div className="statements-section">
          <div className="statements-table-container">
            {visitsLoading ? (
              <div className="loading">Loading farmer visits...</div>
            ) : (
              <table className="statements-table">
                <thead>
                  <tr>
                    <th>DATE & TIME</th>
                    <th>FARMER</th>
                    <th>VILLAGE</th>
                    <th>VISIT TYPE</th>
                    <th>CROP</th>
                    <th>ACRE</th>
                    <th>REASON</th>
                    <th>REMARK</th>
                  </tr>
                </thead>
                <tbody>
                  {farmerVisits.length > 0 ? (
                    farmerVisits.map((visit) => (
                      <tr key={visit.id}>
                        <td style={{ fontSize: 13 }}>
                          {formatDateTime(visit.visitTime)}
                        </td>
                        <td style={{ fontWeight: 500 }}>
                          {visit.farmerName || '—'}
                        </td>
                        <td>{visit.village || '—'}</td>
                        <td>
                          <span
                            className={`ts-badge ${
                              visit.visitType === 'FIRST_VISIT' ? 'working' : 'started'
                            }`}
                          >
                            {visit.visitType === 'FIRST_VISIT'
                              ? 'First Visit'
                              : 'Followup'}
                          </span>
                        </td>
                        <td>{visit.crop  || '—'}</td>
                        <td>{visit.acre  || '—'}</td>
                        <td
                          style={{
                            maxWidth: 160,
                            fontSize: 13,
                            color: visit.reason ? '#c62828' : '#adb5bd',
                            fontStyle: visit.reason ? 'normal' : 'italic',
                          }}
                        >
                          {visit.reason || '—'}
                        </td>
                        <td style={{ maxWidth: 160, fontSize: 13, color: '#495057' }}>
                          {visit.remark || '—'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="no-data">
                        No farmer visits available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TourStatementsView;